/**
 * Toggle switch.
 *
 * `UTSwitch` is fixed at 60x34px with no size option, and the redesign's switches are 40x22 in
 * card footers and 44x24 in the tour editor — so this one takes a size. Built on a real checkbox
 * input, so it is keyboard-operable and announced correctly.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';

const SIZES = {
  sm: { width: 40, height: 22, knob: 16 },
  md: { width: 44, height: 24, knob: 18 },
} as const;

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Required: the switch carries no visible text of its own. */
  'aria-label': string;
  size?: keyof typeof SIZES;
  disabled?: boolean;
  id?: string;
}

export const Switch = ({
  checked,
  onChange,
  'aria-label': ariaLabel,
  size = 'sm',
  disabled = false,
  id,
}: SwitchProps): JSX.Element => {
  const { width, height, knob } = SIZES[size];
  const inset = (height - knob) / 2;

  return (
    <label
      className={css`
        position: relative;
        display: inline-flex;
        flex: 0 0 auto;
        width: ${width}px;
        height: ${height}px;
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        opacity: ${disabled ? 0.5 : 1};
      `}
    >
      <input
        id={id}
        type='checkbox'
        role='switch'
        aria-label={ariaLabel}
        checked={checked}
        disabled={disabled}
        onChange={event => {
          onChange(event.target.checked);
        }}
        className={css`
          position: absolute;
          width: 100%;
          height: 100%;
          margin: 0;
          opacity: 0;
          cursor: inherit;

          &:focus-visible + span {
            box-shadow: var(--shadow-focus);
          }
        `}
      />
      <span
        aria-hidden='true'
        className={css`
          position: absolute;
          inset: 0;
          border-radius: var(--radius-round);
          background: ${checked ? 'var(--color-primary)' : 'var(--color-border-strong)'};
          transition: background var(--duration-fast) var(--ease-standard);

          &::after {
            content: '';
            position: absolute;
            top: ${inset}px;
            left: ${checked ? width - knob - inset : inset}px;
            width: ${knob}px;
            height: ${knob}px;
            border-radius: 50%;
            background: var(--gl-white);
            transition: left var(--duration-fast) var(--ease-standard);
          }
        `}
      />
    </label>
  );
};
